// Libraries
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSelectModule } from '@angular/material/select';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { forkJoin, Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';

// Services
import { ProjectService } from 'src/app/services/api/projects/project.service';
import { TrackerService } from 'src/app/services/api/trackers/tracker.service';
import { TrackService } from 'src/app/services/api/tracks/track.service';
import { UnitService } from 'src/app/services/api/units/unit.service';
import { AuthService } from 'src/app/services/core/auth.service';

// Interfaces
import { Group } from 'src/app/interfaces/api/group';
import { Model } from 'src/app/interfaces/api/model';
import { Project } from 'src/app/interfaces/api/project';
import { Tracker } from 'src/app/interfaces/api/tracker';
import { PostUnit, Unit } from 'src/app/interfaces/api/unit';
import { User } from 'src/app/interfaces/api/user';
import { Config } from 'src/app/interfaces/shared/config';

// Classes
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';

// UI
import { NoDataComponent } from 'projects/ui-library/src/lib/components/no-data/no-data.component';
import { ButtonComponent, SearchBarComponent } from 'ui-library';

// Modals
import { ResourcesModalComponent } from 'src/app/components/project-settings/resources/resources-modal/resources-modal.component';

type DataInput = {
  trackers: Tracker[],
  trackerConfigs?: any[],
  model: Model,
  startDate: string,
  endDate: string,
  mode: string,
  isTrackerChecked?: boolean
  updateTrack: Function
}

@Component({
  selector: 'app-traces-dialog',
  imports: [
    ButtonComponent,
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatPaginatorModule,
    MatSortModule,
    MatTableModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatIconModule,
    MatToolbarModule,
    MatMenuModule,
    MatMenuTrigger,
    TranslateModule,
    SearchBarComponent,
    NoDataComponent
  ],
  standalone: true,
  templateUrl: './traces-dialog.component.html',
  styleUrls: ['./traces-dialog.component.scss']
})

export class TracesDialogComponent implements OnInit, OnDestroy {
  // Services
  private readonly trackService: TrackService = inject(TrackService);
  private readonly projectService: ProjectService = inject(ProjectService)
  private readonly authService: AuthService = inject(AuthService);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly unitService: UnitService = inject(UnitService);
  private readonly trackerService: TrackerService = inject(TrackerService)
  private readonly tools: Tools = inject(Tools);
  private readonly pop: Pop = inject(Pop)
  public dialog: MatDialog = inject(MatDialog);
  public dialogRef: MatDialogRef<TracesDialogComponent> = inject(MatDialogRef<TracesDialogComponent>)
  public data: DataInput = inject(MAT_DIALOG_DATA);

  // Components API
  onAdd = new EventEmitter();
  @ViewChild(MatSort) sort!: MatSort;

  // Variables
  displayedColumns: string[] = ['status', 'name', 'show', 'trace', 'color'];
  trackers: any = {};
  acceptedColors = ["primary", "danger", "success", "info", "warning", "lemon", "teal", "fuchsia", "dark", "info", "white"]
  unitTypeArray: { name: string; value: string }[] = [];
  showCollider = true;
  show = true;
  trace = true;
  colorName = "";
  model!: Model;
  validSearchText: string = "";
  mode = "realtime";
  loadingTrace = false;
  accuracy = 1;
  alertDisplayed = false;
  units: Unit[] = [];
  unitsTrackerIds: string[] = [];
  trackersDialog: Tracker[] = [];
  unitNames: string[] = [];
  groups: Group[] = [];
  unitIcons: { name: string; value: string; icon: string; }[] = [];
  dialogHidden = false;
  devicesCount: number = 0;
  allTrackers: Tracker[] = [];
  destroy$ = new Subject<void>();
  allUnits: Unit[] = [];

  // Signals
  readonly currentUser: Signal<User> = this.authService.currentUser;
  readonly project: Signal<Project> = this.projectService.currentProjectSignal;

  // Computeds
  get showDisconnectTrackers(): boolean {
    return this.project()?.showDisconnectTrackers ?? false;
  }

  set showDisconnectTrackers(value: boolean) {
    this.onAdd.emit({
      trackers: this.data.trackers,
      action: 'showConnect',
      value: value
    });
  }

  listColor: { name: string; value: string }[] = Object.entries(Config.colors)
    .filter(([name, value]) => this.acceptedColors.includes(name))
    .map(([name, value]) => ({ name: name.charAt(0).toUpperCase() + name.slice(1), value }));

  constructor() {
    this.translate.get('home').pipe(takeUntil(this.destroy$)).subscribe(res => {
      this.unitTypeArray = [
        { name: this.translate.instant('units.worker'), value: "worker" },
        { name: this.translate.instant('units.equipment'), value: "equipment" },
        { name: this.translate.instant('units.material'), value: "material" },
        { name: this.translate.instant('units.virtual'), value: "virtual" },
      ]

      this.unitIcons = [
        { name: this.translate.instant('units.antenna'), value: "antenna", icon: "antenna.jpg" }
      ];
    })

    this.trackers = this.data.trackers;
    this.model = this.data.model;
    this.mode = this.data.mode;
  }

  ngOnInit(): void {
    this.showCheckedBox();

    forkJoin({
      units: this.unitService.getUnits().pipe(first()),
      trackers: this.trackerService.getTrackers().pipe(first())
    }).pipe(
      takeUntil(this.destroy$)
    ).subscribe(({ units, trackers }: { units: Unit[], trackers: Tracker[] }) => {
      const initialConfigs = this.data.trackerConfigs;
      this.applyTrackerStatus(trackers, initialConfigs);

      this.units = units;
      this.allUnits = [...units];
      this.trackersDialog = trackers;
      this.unitNames = this.units.map(u => u.name.trim().toLowerCase())

      this.units.forEach(unit => {
        let unitTrackerIds = unit.trackers as unknown as string[];
        let unitTrackers = trackers.filter(t =>
          unitTrackerIds.includes(t.id)
        );

        unit.colorName = unitTrackers.find(t => t.colorName == "green") ? "green" : "red";
        unit.trackers = unitTrackers as any;
      })

      this.unitsTrackerIds = this.units
        .map(u => {
          const firstTracker = u.trackers?.[0];
          return typeof firstTracker === 'string' ? firstTracker : firstTracker?.id;
        })
        .filter((id: string | undefined) => id !== undefined);

      if (!initialConfigs?.length) {
        this.trackerService.getConfigs().pipe(first(), takeUntil(this.destroy$)).subscribe({
          next: (configs) => {
            this.applyTrackerStatus(this.trackersDialog, configs);
            this.units.forEach(unit => {
              const unitTrackers = unit.trackers as Tracker[];
              unit.colorName = unitTrackers.find(t => t.colorName === 'green') ? 'green' : 'red';
            });
          },
          error: (err: any) => console.error(err)
        });
      }
    });
  }

  private applyTrackerStatus(trackers: Tracker[], configs?: any[]): void {
    trackers.forEach(t => {
      const isDelayOk = this.tools.isWithinOfflineDelay(t, configs);
      t.colorName = isDelayOk ? 'green' : 'red';
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  search(text: string = "") {
    this.validSearchText = text.trim().toLowerCase();

    if (this.validSearchText === "") {
      this.units = [...this.allUnits];
    } else {
      this.units = this.allUnits.filter((unit: Unit) => {
        return unit.name.toLowerCase().includes(this.validSearchText);
      });
    }

    this.devicesCount = this.units.length;
  }

  async logConnectedTrackers() {
    // if (this.data.trackers.isConnected == false) {
    //   console.log(`${this.data.trackers.name} is connected`);
    // } else {
    //   console.log(`${this.data.trackers.name} is deconnected`);
    // }
  }

  showCheckedBox() {
    const val = ['show', 'showCollider', 'trace'] as const;

    val.forEach(a => {
      (this as any)[a] = true;
      this.data.trackers.forEach(t => {
        if (!(t as any)[a]) (this as any)[a] = false
      })
    });
  }

  showUnits(evt: { checked: boolean }, unitId: string, val: string) {
    const unit = this.units.find(u => u.id === unitId);

    if (!unit || !unit.trackers || unit.trackers.length === 0) {
      return;
    }

    if (val === 'show') {
      unit.visible = evt.checked;
    } else if (val === 'trace') {
      unit.trace = evt.checked;
    } else if (val === 'showCollider') {
      if (!unit.collider) unit.collider = { visible: false } as any;
      unit.collider.visible = evt.checked;
    }

    this.data.trackers.forEach((t: Tracker) => {
      if (t.unitId == unitId) {
        (t as any)[val] = evt.checked;
      }
    });

    const trackers = this.data.trackers.filter((t: Tracker) => t.unitId == unitId);

    if (trackers.length > 0) {
      this.onAdd.emit({ trackers, action: val });
    }

    this.showCheckedBox();
  }

  showAll(evt: { checked: boolean }) {
    const isChecked = evt.checked;

    this.show = isChecked;
    this.showCollider = isChecked;

    this.units.forEach(unit => {
      if (unit.trackers && unit.trackers.length > 0) {
        unit.visible = isChecked;
        if (unit.collider) {
          unit.collider.visible = isChecked;
        }
      }
    });

    this.data.trackers.forEach((t: any) => {
      const unit = this.units.find(u => u.id === t.unitId);
      if (unit && unit.trackers && unit.trackers.length > 0) {
        t.show = isChecked;
        t.showCollider = isChecked;
      }
    });

    const validTrackers = this.data.trackers.filter(t => {
      if (!t || !t.id) return false;
      const unit = this.units.find(u => u.id === t.unitId);
      return unit && unit.trackers && unit.trackers.length > 0;
    });

    this.onAdd.emit({ trackers: validTrackers, action: 'show' });
    this.showCheckedBox();
  }

  showTrace(evt: { checked: boolean }) {
    const isChecked = evt.checked;

    this.trace = isChecked;

    this.units.forEach(unit => {
      if (unit.trackers && unit.trackers.length > 0) {
        unit.trace = isChecked;
      }
    });

    this.data.trackers.forEach((t: any) => {
      const unit = this.units.find(u => u.id === t.unitId);

      if (unit && unit.trackers && unit.trackers.length > 0) {
        t.trace = isChecked;
      }
    });

    this.onAdd.emit({ trackers: this.data.trackers, action: 'trace' });
    this.showCheckedBox();
  }

  getColorValue(colorKey: string | null | undefined): string {
    return this.tools.getColorValue(colorKey, this.listColor);
  }

  getColorName(colorKey: string | null | undefined): string {
    return this.tools.getColorName(colorKey, this.listColor);
  }

  setUnitColor(evt: { value: string | null }, unitId: string) {
    const unit = this.units.find(u => u.id === unitId);

    if (!unit || !unit.trackers || unit.trackers.length === 0) {
      return;
    }

    const colorValue = evt.value || Config.colors.primary;
    let tracker = this.data.trackers.find((t: Tracker) => t.unitId == unitId);

    if (tracker) {
      tracker.color = colorValue as any;
      this.onAdd.emit({ trackers: [tracker], action: 'color', value: colorValue });
    }
  }

  loadTraces() {
    this.loadingTrace = true;
    let alertDisplayed = false;
    let n = this.data.trackers.length;

    this.data.trackers.forEach((tracker: Tracker) => {
      this.trackService.getPositionByTimePeriod({ tracker: tracker.id, startDate: this.data.startDate, endDate: this.data.endDate, accuracy: this.accuracy })
        .pipe(takeUntil(this.destroy$)).subscribe((result: any[]) => {
          n--;

          if (!alertDisplayed && result.length >= 10000) {
            this.pop.toast(this.translate.instant('viewer.max_key_reached'), "warning", 4000, "trace");
            alertDisplayed = true;
          } else if (!alertDisplayed && n == 0) {
            this.pop.toast(this.translate.instant('viewer.all_traces_loadded'), "success", 4000, "trace");
          }

          if (this.data.updateTrack) {
            result.forEach((r: any[]) => {
              this.data.updateTrack({ tracker: r[0], x: r[1], y: r[2], z: r[3], lon: r[4], lat: r[5] });
            });
          }

          if (n == 0) {
            this.loadingTrace = false;
            this.dialogRef.close();
          }
        }, (err: any) => {
          console.error(err)
        })
    })
  }

  clearTraces() {
    this.onAdd.emit({ trackers: this.data.trackers, action: 'reset-trace' })
  }

  addVirtualResource() {
    let name = "V-" + Math.floor((Math.random() * 10000))

    this.trackerService.addTracker({ name: name, project: this.project().id }).pipe(first()).subscribe({
      next: (res: Tracker) => {
        let unit: PostUnit = {
          name,
          trackers: [res.id],
          type: "virtual",
          lastPosition: {
            lat: this.model.origin.position.lat,
            lon: this.model.origin.position.lon,
            z: this.model.origin.position.z
          }
        }

        this.unitService.addUnit(unit).pipe(takeUntil(this.destroy$)).subscribe({
          next: () => {
            this.dialog.closeAll();
            this.pop.toast(this.translate.instant("trackers.add_success"), "success", 4000, "add");
          }, error: (err: any) => console.error(err)
        });
      }, error: (err: any) => console.error(err)
    });
  }

  addResource() {
    const modal = this.dialog.open(ResourcesModalComponent, {
      width: '800px',
      data: {
        colors: "#3c8dbc",
        trackers: this.trackersDialog,
        usedTrackerIds: this.unitsTrackerIds,
        unitTypeArray: this.unitTypeArray,
        resourceNames: this.unitNames,
        groups: this.groups,
        user: this.currentUser(),
        unitIcons: this.unitIcons,
      }
    });

    modal.afterClosed().subscribe(res => {
      this.dialog.closeAll();
    })
  }

  close() {
    this.dialogRef.close({})
  }
}
